import { X, Cpu, Layers, Calendar, Globe } from 'lucide-react';
import { APP_NAME } from '../config/labels';

export interface AboutInfo {
  profile: string;
  rc_version: string;
  version: string; // build timestamp
}

interface AboutModalProps {
  info: AboutInfo | null;
  onClose: () => void;
}

/**
 * About popup: opened when the user clicks the env/version label in
 * the sidebar. Shows environment details, RC version, build timestamp, and a
 * "Developed by CyboLabs — cybolabs.com" line. No copyright info.
 */
export default function AboutModal({ info, onClose }: AboutModalProps) {
  if (!info) return null;
  const envLabel = info.profile.toUpperCase();
  const buildTs = info.version && info.version !== 'dev' ? info.version : 'dev (local build)';
  // /health already returns rc_version with a leading 'v.' (e.g. v.202609232121);
  // don't double-prefix.
  const rcRaw = info.rc_version && info.rc_version !== 'dev' ? info.rc_version : 'dev';
  const rcLabel = rcRaw.startsWith('v.') ? rcRaw : `v.${rcRaw}`;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="card p-6 w-full max-w-sm mx-4"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="About"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-800">About</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <div className="w-10 h-10 rounded-lg bg-primary-600 text-white flex items-center justify-center font-bold text-lg">
              DR
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-800">{APP_NAME}</div>
              <div className="text-xs text-slate-500">AI-assisted Stock Deposit Review System</div>
            </div>
          </div>

          <div className="space-y-2">
            <Row icon={<Layers size={14} />} label="Environment" value={envLabel} />
            <Row icon={<Cpu size={14} />} label="Version" value={rcLabel} />
            <Row icon={<Calendar size={14} />} label="Build" value={buildTs} />
          </div>

          <div className="pt-3 border-t border-slate-100">
            <a
              href="https://cybolabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              <Globe size={14} />
              Developed by CyboLabs — cybolabs.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-xs text-slate-500">
        {icon}
        {label}
      </span>
      <span className="text-xs font-medium text-slate-700 font-mono">{value}</span>
    </div>
  );
}